import { useState, type FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/daij68jp14a1h3mr754g';

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function ContactForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [feedback, setFeedback] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honey = String(formData.get('contact_alt') ?? '').trim();
    if (honey) {
      setStatus('success');
      setFeedback('已收到你的信息，编辑部会尽快查看。');
      form.reset();
      return;
    }

    setStatus('submitting');
    setFeedback('');

    try {
      const payload = new URLSearchParams();
      formData.forEach((value, key) => {
        if (typeof value === 'string' && value.trim() !== '') {
          payload.append(key, value);
        }
      });

      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');
      const ok = response.ok && parsed?.code === 'OK' && !isSpam;

      if (ok) {
        setStatus('success');
        setFeedback('已收到你的信息，编辑部会尽快查看并回复。');
        form.reset();
      } else {
        setStatus('error');
        setFeedback(serverMsg || '提交失败，请稍后重试，或直接发送邮件给我们。');
      }
    } catch {
      setStatus('error');
      setFeedback('网络异常，提交未成功，请稍后重试。');
    }
  };

  return (
    <form
      id="contact-inquiry-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7"
    >
      <h2 className="font-heading text-xl font-bold text-foreground-950">提交线索或留言</h2>
      <p className="mt-2 text-sm leading-relaxed text-foreground-600">
        请尽量说明信息来源与可核对的时间点，越具体的内容越有助于我们判断与跟进。
      </p>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="contact-name" className="text-sm font-medium text-foreground-800">
            称呼 <span className="text-primary-600">*</span>
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            maxLength={40}
            autoComplete="name"
            placeholder="怎么称呼你"
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 placeholder:text-foreground-500"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="contact-email" className="text-sm font-medium text-foreground-800">
            邮箱 <span className="text-primary-600">*</span>
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            autoComplete="email"
            placeholder="便于我们回复你"
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 placeholder:text-foreground-500"
          />
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-1.5">
        <label htmlFor="contact-subject" className="text-sm font-medium text-foreground-800">
          留言类型
        </label>
        <select
          id="contact-subject"
          name="subject"
          defaultValue="提供线索"
          className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
        >
          <option value="提供线索">提供线索</option>
          <option value="内容更正">内容更正</option>
          <option value="合作咨询">合作咨询</option>
          <option value="读者反馈">读者反馈</option>
          <option value="其他">其他</option>
        </select>
      </div>

      <div className="mt-4 flex flex-col gap-1.5">
        <label htmlFor="contact-message" className="flex items-center justify-between text-sm font-medium text-foreground-800">
          <span>
            详细说明 <span className="text-primary-600">*</span>
          </span>
          <span className="text-xs font-normal text-foreground-500">最多 500 字</span>
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="发生了什么、大致时间、有哪些可核对的公开信息……"
          className="resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none transition-colors focus:border-primary-400 placeholder:text-foreground-500"
        />
      </div>

      <input
        type="text"
        name="contact_alt"
        className="form-field-quiet-guard"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs leading-relaxed text-foreground-600">
          提交即表示你同意我们在核实范围内使用该信息。
        </p>
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <i className={status === 'submitting' ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'} aria-hidden="true" />
          {status === 'submitting' ? '正在提交…' : '提交信息'}
        </button>
      </div>

      {status !== 'idle' && status !== 'submitting' && (
        <p
          role="status"
          className={`mt-4 flex items-start gap-2 rounded-md px-4 py-3 text-sm ${
            status === 'success'
              ? 'bg-secondary-100 text-secondary-900'
              : 'bg-primary-100 text-primary-800'
          }`}
        >
          <i className={status === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'} aria-hidden="true" />
          <span>{feedback}</span>
        </p>
      )}
    </form>
  );
}